import { useState, useRef, useEffect } from "react";

import { EyeIcon, PencilIcon } from "@heroicons/react/24/outline";

const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Enter text...",
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);

  useEffect(() => {
    if (editorRef.current && !showPreview) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value, showPreview]);

  const handleCommand = (command, value = null) => {
    if (!editorRef.current) return;

    // Focus the editor first
    editorRef.current.focus();

    // Execute the command
    const result = document.execCommand(command, false, value);

    // Update the value
    if (result && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleFormat = (tag) => {
    if (!editorRef.current) return;

    // Focus the editor
    editorRef.current.focus();

    const selection = window.getSelection();

    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      // Wrap selected text in the tag
      document.execCommand(
        "insertHTML",
        false,
        `<${tag}>${selectedText}</${tag}>`
      );
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const selection = window.getSelection();

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const text = range.toString();

      // Save the current selection/cursor position
      setSavedSelection({
        startContainer: range.startContainer,
        startOffset: range.startOffset,
        endContainer: range.endContainer,
        endOffset: range.endOffset,
        text,
      });

      setSelectedText(text);
      setShowLinkDialog(true);
    } else {
      // No selection, still show dialog
      setSavedSelection(null);
      setSelectedText("");
      setShowLinkDialog(true);
    }
  };

  const handleInsertLink = () => {
    if (linkUrl && editorRef.current) {
      // Focus the editor
      editorRef.current.focus();

      // Restore the saved selection if it exists
      if (savedSelection) {
        const selection = window.getSelection();
        const range = document.createRange();

        try {
          range.setStart(
            savedSelection.startContainer,
            savedSelection.startOffset
          );
          range.setEnd(savedSelection.endContainer, savedSelection.endOffset);
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          // If the saved selection is invalid, just insert at current position
        }
      }

      // Use execCommand to insert the link at the current position
      const linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-600 underline">${selectedText || linkUrl}</a>`;

      document.execCommand("insertHTML", false, linkHTML);

      // Update the onChange
      onChange(editorRef.current.innerHTML);
    }

    setShowLinkDialog(false);
    setLinkUrl("");
    setSelectedText("");
    setSavedSelection(null);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");

    document.execCommand("insertText", false, text);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const ToolbarButton = ({ icon, command, commandValue, title, onClick }) => (
    <button
      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
      title={title}
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        if (onClick) {
          onClick();
        } else {
          handleCommand(command, commandValue);
        }
      }}
    >
      <i className={`fas fa-${icon} text-gray-700 dark:text-gray-300`} />
    </button>
  );

  const ToolbarSeparator = () => (
    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
  );

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div
        className={`flex items-center flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600 ${showPreview ? "opacity-50 pointer-events-none" : ""}`}
      >
        <ToolbarButton command="bold" icon="bold" title="Bold (Ctrl+B)" />
        <ToolbarButton command="italic" icon="italic" title="Italic (Ctrl+I)" />
        <ToolbarButton
          command="underline"
          icon="underline"
          title="Underline (Ctrl+U)"
        />
        <ToolbarButton
          command="strikethrough"
          icon="strikethrough"
          title="Strikethrough"
        />

        <ToolbarSeparator />

        <ToolbarButton
          command="formatBlock"
          commandValue="<h2>"
          icon="heading"
          title="Heading"
        />
        <ToolbarButton
          command="formatBlock"
          commandValue="<p>"
          icon="paragraph"
          title="Paragraph"
        />

        <ToolbarSeparator />

        <ToolbarButton
          command="insertUnorderedList"
          icon="list-ul"
          title="Bullet List"
        />
        <ToolbarButton
          command="insertOrderedList"
          icon="list-ol"
          title="Numbered List"
        />

        <ToolbarSeparator />

        <ToolbarButton icon="link" title="Insert Link" onClick={insertLink} />
        <ToolbarButton command="unlink" icon="unlink" title="Remove Link" />

        <ToolbarSeparator />

        <ToolbarButton
          icon="code"
          title="Code"
          onClick={() => handleFormat("code")}
        />
        <ToolbarButton
          command="formatBlock"
          commandValue="<blockquote>"
          icon="quote-left"
          title="Quote"
        />

        <ToolbarSeparator />

        <ToolbarButton
          command="justifyLeft"
          icon="align-left"
          title="Align Left"
        />
        <ToolbarButton
          command="justifyCenter"
          icon="align-center"
          title="Align Center"
        />
        <ToolbarButton
          command="justifyRight"
          icon="align-right"
          title="Align Right"
        />

        <ToolbarSeparator />

        <ToolbarButton
          command="removeFormat"
          icon="eraser"
          title="Clear Formatting"
        />
      </div>

      {/* Preview Toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {showPreview ? "Preview Mode" : "Edit Mode"}
        </span>
        <button
          className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
          type="button"
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? (
            <>
              <PencilIcon className="w-4 h-4" />
              Edit
            </>
          ) : (
            <>
              <EyeIcon className="w-4 h-4" />
              Preview
            </>
          )}
        </button>
      </div>

      {/* Editor/Preview */}
      {showPreview ? (
        <div
          className="min-h-[150px] max-h-[400px] overflow-y-auto p-4 rich-text-preview"
          dangerouslySetInnerHTML={{
            __html:
              value || '<p class="text-gray-500">No content to preview</p>',
          }}
        />
      ) : (
        <div
          contentEditable
          className="min-h-[150px] max-h-[400px] overflow-y-auto p-4 focus:outline-none rich-text-editor"
          data-placeholder={placeholder}
          ref={editorRef}
          style={{
            outline: "none",
          }}
          onInput={(e) => onChange(e.target.innerHTML)}
          onPaste={handlePaste}
        />
      )}

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Insert Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Text</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  placeholder="Link text"
                  type="text"
                  value={selectedText}
                  onChange={(e) => setSelectedText(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">URL</label>
                <input
                  autoFocus
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  placeholder="https://example.com"
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  type="button"
                  onClick={() => {
                    setShowLinkDialog(false);
                    setLinkUrl("");
                    setSelectedText("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  type="button"
                  onClick={handleInsertLink}
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
